/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import {
	isPreviewCardAction,
	isSupportedStreamAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import { isJiraCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jira-actions';
import { isJsmCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jsm-actions';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';
import {
	type AgentVersionInput,
	type AlphaExtendedThinkingMode,
	type RovoLabsConfirmedTools,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type MessageAction as APIMessageAction,
	type MessageActionInvocationStatus,
	type MessageActionPayload,
	type MessageActionResolutions,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	getActionInvocationStatus,
	type StoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { useCustomMessageActionBroker } from '../message-actions';

import messages from './messages';
import { type ActionContentProcessorFn } from './types';

const styles = cssMap({
	wrapper: {
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.150'),
	},
});

const isActionEnded = (actionStatus?: MessageActionInvocationStatus | 'idle') => {
	if (!actionStatus) {
		return false;
	}
	return ['finished', 'errored', 'cancelled'].includes(actionStatus);
};

export type MessageActionsAnalytics = {
	onErrorRender?: (action: APIMessageAction, args: { error: Error; info: React.ErrorInfo }) => void;
	onSuccessRender?: (action: APIMessageAction) => void;
};

/**
 * Structural shape of the params accepted by the host's
 * `sendMessageConfirmedTools` action. Captured here so the container
 * does not need to import from the web host package.
 */
export type SendMessageConfirmedToolsParams = {
	conversationId?: string;
	agentId?: string;
	confirmed_tools: RovoLabsConfirmedTools;
	messageSources?: Record<string, unknown>;
	storeUserMessages?: boolean;
	webSearchEnabled?: boolean;
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
	includeAIFeatureContext?: boolean;
	agentVersion?: AgentVersionInput;
};

export type MessageActionsContainerProps = {
	actions: APIMessageAction[];
	onActioned: <TActionCallbackReturnType = unknown>(
		payload: MessageActionPayload,
		actionContentProcessor?: ActionContentProcessorFn<TActionCallbackReturnType>,
		options?: { skipSuccessOptimisticAssistantResponse?: boolean },
	) => Promise<TActionCallbackReturnType | undefined>;
	messageId: string;
	messageType: string;
	invokedActions?: StoreAssistant['invokedActions'];
	recipientAgentNamedId?: string;
	recipientAgentId?: string;
	recipientAgentName?: string;
	conversationId?: string;
	jiraActivationId?: string;
	isHistoricalMessage?: boolean;
	isLastMessage?: boolean;
	isLastActionMessage?: boolean;
	/**
	 * Executed interaction mode resolved from the originating assistant
	 * message's `message_metadata.interaction_mode_executed`. Forwarded to
	 * every action renderer via the broker so they can set
	 * `attributes.interactionModeExecuted` on AIMAU events.
	 */
	interactionModeExecuted?: string;
	/**
	 * Whether Max mode (task mode) was enabled when the message that produced
	 * these actions was sent. Forwarded to every action renderer via the broker
	 * so they can include `isMaxMode` in their analytics events.
	 */
	taskModeEnabled?: boolean;

	// Injected host context (previously read from web ai-mate hooks)
	cloudId: string;
	orgId?: string;
	aaid?: string;
	productKey: string;
	experienceId?: string;
	renderingTouchpoint?: string;
	isContentPreviewSupported: boolean;
	sendMessageConfirmedTools: (params: SendMessageConfirmedToolsParams) => Promise<void>;
	resolveActionsBulk?: (
		actionKey: string,
		actionResolution: MessageActionResolutions,
		invocationIds: string[],
		invocationIdToCreatedIssueKey?: Record<string, string>,
	) => void;
	messageActionsAnalytics?: MessageActionsAnalytics;
};

export const MessageActionsContainer = ({
	actions,
	onActioned,
	invokedActions,
	recipientAgentNamedId,
	recipientAgentId,
	recipientAgentName,
	conversationId,
	messageId,
	messageType,
	jiraActivationId,
	isHistoricalMessage,
	isLastMessage,
	isLastActionMessage,
	interactionModeExecuted,
	taskModeEnabled,
	cloudId,
	orgId,
	aaid,
	productKey,
	experienceId,
	renderingTouchpoint,
	isContentPreviewSupported,
	sendMessageConfirmedTools,
	resolveActionsBulk,
	messageActionsAnalytics,
}: MessageActionsContainerProps): JSX.Element | (JSX.Element | null)[] | null => {
	di(useCustomMessageActionBroker, getActionInvocationStatus);

	const { formatMessage } = useIntl();

	const handleDefaultActionConfirmed = useCallback(
		<TActionResult = unknown,>(action: APIMessageAction) =>
			onActioned<TActionResult>({
				context: {
					messageId,
					data: action.data,
					invocationId: action.invocationId,
					key: action.key,
					resolution: {
						type: 'confirm',
					},
					recipient_agent_named_id: recipientAgentNamedId,
				},
			}),
		[messageId, onActioned, recipientAgentNamedId],
	);

	const handleDefaultActionCancelled = useCallback(
		<TActionResult = unknown,>(action: APIMessageAction) =>
			onActioned<TActionResult>({
				context: {
					messageId,
					data: action.data,
					invocationId: action.invocationId,
					key: action.key,
					resolution: {
						type: 'cancel',
					},
					recipient_agent_named_id: recipientAgentNamedId,
				},
			}),
		[messageId, onActioned, recipientAgentNamedId],
	);

	const handleDefaultActionActionedElsewhere = useCallback(
		<TActionResult = Promise<unknown>,>(action: APIMessageAction, message?: string) =>
			onActioned<TActionResult>({
				context: {
					messageId,
					data: action.data,
					invocationId: action.invocationId,
					key: action.key,
					resolution: {
						type: 'actioned',
						message,
					},
					recipient_agent_named_id: recipientAgentNamedId,
				},
			}),
		[messageId, onActioned, recipientAgentNamedId],
	);

	const handleCustomDataActionConfirmed = useCallback(
		<TActionResult = unknown,>(
			action: APIMessageAction,
			data: APIMessageAction['data'],
			actionContentProcessor?: ActionContentProcessorFn<TActionResult>,
		) =>
			onActioned<TActionResult>(
				{
					context: {
						messageId,
						data: data,
						invocationId: action.invocationId,
						key: action.key,
						resolution: {
							type: 'confirm',
						},
						recipient_agent_named_id: recipientAgentNamedId,
					},
				},
				actionContentProcessor,
			),
		[messageId, onActioned, recipientAgentNamedId],
	);

	const handleCustomDataActionActionedElsewhere = useCallback(
		<TActionResult = unknown,>(
			action: APIMessageAction,
			data: APIMessageAction['data'],
			message?: string,
			skipSuccessOptimisticAssistantResponse?: boolean,
		) =>
			onActioned<TActionResult>(
				{
					context: {
						messageId,
						data,
						invocationId: action.invocationId,
						key: action.key,
						resolution: {
							type: 'actioned',
							message,
						},
						recipient_agent_named_id: recipientAgentNamedId,
					},
				},
				undefined,
				{ skipSuccessOptimisticAssistantResponse },
			),
		[messageId, onActioned, recipientAgentNamedId],
	);

	const getReadonly = useCallback(
		(action: APIMessageAction) =>
			isActionEnded(action.status) ||
			isActionEnded(getActionInvocationStatus(action.invocationId, invokedActions)),
		[invokedActions],
	);

	const getStatus = useCallback(
		(action: APIMessageAction) => getActionInvocationStatus(action.invocationId, invokedActions),
		[invokedActions],
	);

	const getActionLoading = useCallback(
		(action: APIMessageAction) =>
			getActionInvocationStatus(action.invocationId, invokedActions) === 'started',
		[invokedActions],
	);

	const getActionError = useCallback(
		(action: APIMessageAction) =>
			getActionInvocationStatus(action.invocationId, invokedActions) === 'errored',
		[invokedActions],
	);

	const handleCancel = useCallback(
		(action: APIMessageAction) => {
			// TODO: Remove the legacy `onCancel` path once the tools resumption migration is
			// cleaned up (kill switch and experiment removed).
			if (
				FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
			) {
				const idleActions = actions.filter((idleAction) => getStatus(idleAction) === 'idle');
				sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: { interactionSource: 'messageActionsContainerCancel' },
					confirmed_tools: {
						message_id: messageId ?? '',
						tools: idleActions.map((idleAction) => buildToolFromAction(idleAction, 'cancelled')),
					},
				});
				return;
			}

			handleDefaultActionCancelled(action);
		},
		[
			actions,
			getStatus,
			sendMessageConfirmedTools,
			recipientAgentId,
			conversationId,
			messageId,
			handleDefaultActionCancelled,
		],
	);

	const getCancelButton = useCallback(
		(action: APIMessageAction, isDisabled: boolean = false) => {
			return (
				<Button
					isDisabled={isDisabled || getActionLoading(action)}
					appearance="subtle"
					onClick={() => handleCancel(action)}
				>
					{formatMessage(messages.cancelActionButton)}
				</Button>
			);
		},
		[getActionLoading, handleCancel, formatMessage],
	);

	const customAction = useCustomMessageActionBroker(
		actions,
		{
			getStatus,
			getReadonly, // TODO: consolidate action statuses in AIM-3229
			onConfirm: handleDefaultActionConfirmed,
			onCancel: handleDefaultActionCancelled,
			onActioned: handleDefaultActionActionedElsewhere,
			onCustomDataConfirm: handleCustomDataActionConfirmed,
			onCustomDataActioned: handleCustomDataActionActionedElsewhere,
			ContentRenderer,
			getCancelButton,
			cloudId,
			...(orgId ? { orgId } : {}),
			aaid,
			recipientAgentNamedId,
			recipientAgentId,
			recipientAgentName,
			conversationId,
			messageId,
			productKey,
			experienceId,
			sendMessageConfirmedTools,
			resolveActionsBulk,
			jiraActivationId,
			isHistoricalMessage,
			interactionModeExecuted,
			taskModeEnabled,
			...(fg('fe_enable_sa_externally') && { renderingTouchpoint }),
			...(fg('jira-ai-agentic-planning-technical-changes') && {
				isLastMessage,
			}),
			...(fg('jsm-kb-action-message-prop') && {
				isLastActionMessage,
			}),
		},
		messageActionsAnalytics,
	);

	if (customAction) {
		return (
			<Box xcss={styles.wrapper}>
				<ErrorBoundary fallbackRender={() => <ChatGenericError errorType="generic" />}>
					{customAction}
				</ErrorBoundary>
			</Box>
		);
	}

	if (messageType === 'FINAL_RESPONSE' && fg('aifc_create_enabled')) {
		const streamActions = actions.filter((action) => isSupportedStreamAction({ key: action.key }));
		if (streamActions.length > 0) {
			// For all streaming actions, we do not have a confirm/cancel phase in the UI
			return null;
		}
	}

	if (messageType === 'FINAL_RESPONSE' && fg('jira_aifc_enabled')) {
		const jiraCreatableActions = actions.filter((action) =>
			isJiraCreatableAction({ key: action.key }),
		);
		if (jiraCreatableActions.length > 0) {
			// Don't show the confirm button in the chat for Jira AIFC actions
			return null;
		}
	}

	if (messageType === 'FINAL_RESPONSE' && fg('surveys-aifc')) {
		const jsmCreatableActions = actions.filter((action) =>
			isJsmCreatableAction({ key: action.key }),
		);
		if (jsmCreatableActions.length > 0) {
			// Don't show the confirm button in the chat for JSM AIFC actions
			return null;
		}
	}

	// Don't show confirm/cancel for previewable actions when preview cards are
	// enabled - this allows any product where AIFC is disabled to show preview cards for
	// previewable actions where we can then open them in Atlassian Home
	if (
		messageType === 'FINAL_RESPONSE' &&
		!isContentPreviewSupported &&
		!fg('aifc_create_enabled')
	) {
		const previewableActions = actions.filter((action) => isPreviewCardAction(action));

		if (previewableActions.length > 0) {
			// Don't show confirm/cancel buttons - render preview cards instead
			return null;
		}
	}

	return actions.map((action) => {
		const actionInvocationStatus = getActionInvocationStatus(action.invocationId, invokedActions);

		if (
			!action ||
			(!!action.status && action.status !== 'planned') ||
			actionInvocationStatus === 'finished' ||
			actionInvocationStatus === 'cancelled'
		) {
			return null;
		}

		const actionLoading = getActionLoading(action);
		const actionError = getActionError(action);

		return (
			<Inline key={action.invocationId} space="space.100" xcss={styles.wrapper} alignBlock="center">
				<Button isDisabled={actionLoading} onClick={() => handleDefaultActionConfirmed(action)}>
					{formatMessage(messages.confirmActionButton)}
				</Button>
				{getCancelButton(action)}

				{actionLoading && (
					<Spinner size="medium" interactionName="message-action-loading-spinner" />
				)}
				{actionError && (
					<WarningIcon
						color="currentColor"
						spacing="spacious"
						label={formatMessage(messages.erroredActionButton)}
					/>
				)}
			</Inline>
		);
	});
};
